import { getAmUserId } from "../../Helpers";

export const replaceHtmlEntities = (obj) => {
  if (typeof obj === "string") {
    const cleaned = obj.replace(/amp;/, " ").replace(/amp;/g, "");
    const tempElement = document.createElement("div");
    tempElement.innerHTML = cleaned;
    return tempElement.innerText;
  }
  if (Array.isArray(obj)) return obj.map(replaceHtmlEntities);
  if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        replaceHtmlEntities(value),
      ]),
    );
  }
  return obj;
};

export const userDataFormat = (data, extAttributes, itemId) => {
  if (data.roleId === "defaultRole") {
    data.roleId = "";
  }
  const formatted = {
    city: data?.city || "",
    administrator: data.userType === 1 ? false : true,
    attributes: extAttributes || {},
    sendNotification: { estimate: true },
    coordinateFormat: data.coordinateFormat || "",
    deviceLimit: data.deviceLimit || "",
    deviceReadonly:
      data.deviceReadonly === "on" || data.deviceReadonly === true,
    disabled: data.disabled === "on" || data.disabled === true,
    email: String(data.email || "").trim(),
    expirationTime: data.expirationTime || null,
    id: itemId || 0,
    latitude: data.latitude || 0,
    limitCommands: data.limitCommands === "on" || data.limitCommands === true,
    login: data.login,
    longitude: data.longitude || 0,
    map: data.map,
    name: String(data.name || "").trim(),
    password: data.password,
    phone: data.phone,
    poiLayer: data.poiLayer,
    readonly: data.readonly === "on" || data.readonly === true,
    token: data.token,
    twelveHourFormat:
      data.twelveHourFormat === "on" || data.twelveHourFormat === true,
    userLimit: data.userLimit || 3,
    vehicleLimit: data.vehicleLimit || "",
    zoom: data.zoom || 0,
    roleId: data.roleId ? data.roleId : data.userType === 1 ? 2 : 3,
    userType: data.userType,
    accountId: data.accountId || 0,
    parentId: data.parentId || 0,
  };
  if (data.customerType) {
    formatted.customerType = parseInt(data.customerType, 10);
  }
  return formatted;
};

export const cleanAccountAttributes = (accountsform, form) => {
  const cleaned = { ...accountsform };
  if (form?.userType === 3) {
    cleaned.accountControl = Boolean(accountsform?.accountControl);
  } else {
    delete cleaned.accountControl;
  }
  if (form?.status === "credit" || !cleaned.c_agreedAdvancePayment) {
    cleaned.c_agreedAdvancePayment = 0;
  }
  delete cleaned.c_am;
  return cleaned;
};

export const buildAccountPayload = (form, accountsform, extras = {}) => {
  const amUserId = getAmUserId({
    managerAccountSelect: extras.managerAccountSelect,
    form,
  });
  return {
    name: form.accountName,
    email: form.accountEmail,
    nameArabic: form.nameArabic,
    monthlyRevenueTarget: form.monthlyRevenueTarget,
    yearlyRevenueTarget: form.yearlyRevenueTarget,
    description: form.accountDescription || "",
    contact: form.accountContact || "",
    customerNumber: form.customerNumber || "",
    contractName: form.contractName || "",
    contractNumber: form.contractNumber || "",
    vatPercentage: form.vatPercentage || 0,
    ...(form.userType === 3 ? { markup: form.markup || 0 } : {}),
    creditLimit: form.status === "advance" ? 0 : form.creditLimit || 0,
    creditPeriod: form.creditPeriod || "",
    contractStartDate: form.contractStartDate || "",
    contractEndDate: form.contractEndDate || "",
    settleDate: form.settleDate || " ",
    defaultSatisfactionDate: form.defaultSatisfactionDate || " ",
    contractType: form.contractType || 1,
    status: form.status || "",
    garageNumber: form.garage?.garageNumber || "",
    pacContractId: form.pacContractId || "",
    ...(form.companyId ? { companyId: form.companyId } : {}),
    ...(form.userType === 3 ? { amUserId } : {}),
    ...(form.userType === 4
      ? {
          customerType: form.customerType,
          garageNumber: form.garage?.garageNumber || form.garageNumber || "",
        }
      : {}),
    attributes: cleanAccountAttributes(accountsform, form),
  };
};

export const buildCreatePayload = (form, accountsform, extras = {}) => {
  let attributes = { ...(form.attributes || {}) };
  if (form.parentId) attributes = { ...attributes, parentId: form.parentId };
  if (form.userType === 1) {
    attributes = {
      ...attributes,
      vehicleId: extras.selectedVehicle?.id || "",
    };
  } else {
    delete attributes.isDriverActive;
    delete attributes.vehicleId;
  }
  const user = userDataFormat(form, attributes);
  if (form.userType === 3) {
    const amUserId = getAmUserId({
      managerAccountSelect: extras.managerAccountSelect,
      form,
    });
    if (amUserId != null) user.amUserId = amUserId;
  }
  if (form.userType > 2) {
    return {
      url: "/api/accounts",
      body: {
        account: buildAccountPayload(form, accountsform, extras),
        user,
      },
    };
  }
  return { url: "/api/users/", body: user };
};

export const accountDataFormat = (data, form, attributes) => {
  const newAttributes = {
    c_buildingNoArabic: "",
    c_addressArabic: "",
    c_streetNameArabic: "",
    c_cityArabic: "",
    c_taxNumber: "",
    ...attributes,
  };
  if (form?.userType === 3) {
    newAttributes.accountControl = Boolean(attributes?.accountControl);
  }
  if (form.status === "credit" || !newAttributes.c_agreedAdvancePayment) {
    newAttributes.c_agreedAdvancePayment = 0;
  }
  delete newAttributes.c_am;
  const amUserId = getAmUserId({ form, account: data });
  const contactValue = (value) => {
    if (!value) return "";
    return Array.isArray(value) ? (value.length ? String(value) : "") : String(value);
  };
  return {
    attributes: newAttributes,
    amUserId,
    contact: contactValue(form.accountContact || data.contact),
    contractType: form?.contractType || 1,
    created: data.created || form.created,
    contractName: form?.contractName || "",
    contractNumber: form?.contractNumber || "",
    pacContractId: form?.pacContractId || "",
    garageNumber: form?.garage?.garageNumber || form?.garageNumber || "",
    vatPercentage: form?.vatPercentage || 0,
    ...(form?.userType === 3 ? { markup: form?.markup || 0 } : {}),
    status: form?.status || 0,
    contractStartDate: form?.contractStartDate || "",
    settleDate: form?.settleDate || "",
    defaultSatisfactionDate: form?.defaultSatisfactionDate || "",
    contractEndDate: form?.contractEndDate || "",
    customerNumber: form?.customerNumber || "",
    creditLimit: form.status === "advance" ? 0 : form?.creditLimit || 0,
    creditPeriod: form?.creditPeriod || "",
    description: form.accountDescription || data.description,
    email: form.accountEmail,
    id: data.id || 0,
    modified: data.modified || form.modified,
    name: form.accountName,
    active: form.active ? true : false,
    nameArabic: form?.nameArabic || "",
    yearlyRevenueTarget: form?.yearlyRevenueTarget || "",
    monthlyRevenueTarget:
      form?.monthlyRevenueTarget || data?.monthlyRevenueTarget || "",
    monthlyRevenueTargets:
      form?.monthlyRevenueTargets || data?.monthlyRevenueTargets || {},
    parentId: data.parentId || 0,
    settingId: data?.settingId || "",
    activeDate: data?.activeDate || "",
    inactiveDate: data?.inactiveDate || "",
    ...(data?.companyId ? { companyId: data.companyId } : {}),
  };
};
